import { renderQrCode } from "./qr-code.js";
export class NodePairingViewElement extends HTMLElement {
    #state = { status: "working" };
    set state(value) {
        this.#state = value;
        this.#render();
    }
    get state() {
        return this.#state;
    }
    connectedCallback() {
        this.#render();
    }
    #render() {
        const root = this.shadowRoot ?? this.attachShadow({ mode: "open" });
        root.replaceChildren();
        const style = document.createElement("style");
        // Styled against the vendored original-skin tokens (skin/css/legacy-numbered.css +
        // styles/tokens.css), NOT literals: this view renders inside the shell's shadow tree,
        // so the skin sheet's `:host, :root` token declarations (adopted by <node-app-shell>'s
        // root, plus `getSkinGlobalSheet()` at the document level) inherit straight through
        // this element's own shadow boundary — and theme-init's `dark`/`menu-dark` classes on
        // the shell host flip every token here automatically. The `light-dark()` pair below
        // rides the shell's own `color-scheme` resolution (`:host(.dark){color-scheme:dark}`,
        // skin-stylesheet.ts) so it follows the RESOLVED theme, including an explicit
        // `nodeos-theme` override that disagrees with the OS. Literal fallbacks mirror the
        // light-theme token values for any host page that never adopted the skin (same
        // `var(--token, <literal>)` convention as system/pwa/index.html). The QR quiet zone
        // stays literal #FFFFFF in BOTH themes — scannability over theming (the SVG itself
        // hardcodes white/black modules, qr-code.ts).
        style.textContent = `
      :host { display: block; min-height: 100vh; background: var(--bg-0, #FFFFFF); color: var(--ink-hi, #0A0A0A); font: 16px/1.5 var(--ui-font, Inter, system-ui, sans-serif); }
      main { max-width: 42rem; margin: 0 auto; padding: clamp(2rem, 8vw, 7rem) 1.25rem; }
      section { display: grid; gap: 1rem; padding: clamp(1.25rem, 4vw, 2.25rem); border: 1px solid var(--hr, rgba(10, 10, 10, 0.07)); border-radius: var(--ui-radius-lg, 14px); background: var(--bg-raised, #FFFFFF); box-shadow: var(--ui-shadow-2, 0 1px 1px rgba(10, 10, 10, 0.06), 0 24px 48px -24px rgba(10, 10, 10, 0.4)); }
      h1, p { margin: 0; } h1 { font: 400 clamp(1.75rem, 5vw, 2.75rem)/1.15 var(--ui-serif, "Instrument Serif", serif); } p { color: var(--ink-mid, #545454); }
      .code { font: 700 clamp(2rem, 10vw, 4rem)/1 var(--ui-mono, "JetBrains Mono", ui-monospace, monospace); letter-spacing: .13em; color: var(--ink-hi, #0A0A0A); }
      .qr { display: block; box-sizing: border-box; width: min(100%, 18rem); aspect-ratio: 1; padding: .75rem; border-radius: var(--ui-radius, 10px); border: 1px solid var(--hr, rgba(10, 10, 10, 0.07)); background: #FFFFFF; }
      .actions { display: flex; flex-wrap: wrap; gap: .75rem; }
      button { min-height: 2.75rem; border: 1px solid var(--ink-hi, #0A0A0A); border-radius: var(--ui-radius, 10px); padding: .65rem 1.1rem; font: 500 .9375rem/1.2 var(--ui-font, Inter, system-ui, sans-serif); color: var(--bg-0, #FFFFFF); background: var(--ink-hi, #0A0A0A); cursor: pointer; transition: background 0.18s var(--ease-out, ease-out), border-color 0.18s var(--ease-out, ease-out); }
      button:hover { background: var(--c-coral-deep, #B85551); border-color: var(--c-coral-deep, #B85551); color: #FFFFFF; }
      button:focus-visible, input:focus-visible { outline: 2px solid var(--ds-accent, #D4635F); outline-offset: 2px; }
      label { display: grid; gap: .4rem; color: var(--ink-mid, #545454); font-size: .875rem; }
      input { min-height: 2.75rem; box-sizing: border-box; width: 100%; border: 1px solid var(--hr-strong, rgba(10, 10, 10, 0.12)); border-radius: var(--ui-radius, 10px); padding: .6rem .75rem; color: var(--ink-hi, #0A0A0A); background: var(--bg-0, #FFFFFF); font: 1rem/1.5 var(--ui-font, Inter, system-ui, sans-serif); }
      input::placeholder { color: var(--ink-mid, #545454); }
      [role=alert] { color: light-dark(var(--c-coral-deep, #B85551), var(--c-coral, #DF6662)); }
      @media (prefers-reduced-motion: reduce) { * { scroll-behavior: auto !important; transition-duration: 0.01ms !important; } }
      @media (forced-colors: active) { section, button, input { border: 1px solid CanvasText; } }
    `;
        const main = document.createElement("main");
        const section = document.createElement("section");
        section.setAttribute("aria-live", "polite");
        const heading = document.createElement("h1");
        const copy = document.createElement("p");
        const actions = document.createElement("div");
        actions.className = "actions";
        if (this.#state.status === "working") {
            heading.textContent = "Securing this browser";
            copy.textContent = "Creating or recovering the browser-owned device identity…";
        }
        else if (this.#state.status === "ready") {
            heading.textContent = "Device connected";
            copy.textContent = "The scoped session is ready.";
        }
        else if (this.#state.status === "revoked") {
            heading.textContent = "This device was revoked";
            copy.textContent = "Session and delegation material have been cleared. Create a new identity to pair again.";
            actions.append(this.#button("Pair as a new device", "recover"));
        }
        else if (this.#state.status === "error") {
            heading.textContent = "Pairing could not continue";
            copy.textContent = this.#state.error ?? "Try again when the home node is available.";
            copy.setAttribute("role", "alert");
            actions.append(this.#button("Try again", "retry"));
        }
        else {
            heading.textContent = this.#state.status === "migration" ? "Approve device migration" : "Approve this browser";
            copy.textContent = this.#state.status === "migration"
                ? "Sign the challenge on the existing owner surface. The seed never enters this browser."
                : "On a trusted device, approve this QR request. The code is derived from the same approval operation.";
            if (this.#state.shortCode !== undefined) {
                const code = document.createElement("output");
                code.className = "code";
                code.setAttribute("aria-label", `Pairing code ${this.#state.shortCode.split("").join(" ")}`);
                code.textContent = this.#state.shortCode;
                section.append(heading, copy, code);
            }
            else {
                section.append(heading, copy);
            }
            if (this.#state.qrPayload !== undefined && this.#state.qrModules !== undefined) {
                const qr = renderQrCode(this.#state.qrModules, this.#state.qrPayload);
                qr.setAttribute("class", "qr");
                section.append(qr);
            }
            if (this.#state.expiresAt !== undefined) {
                const expiry = document.createElement("p");
                expiry.textContent = `Expires ${new Date(this.#state.expiresAt).toLocaleString()}`;
                section.append(expiry);
            }
            if (this.#state.status === "migration") {
                const signature = this.#input("Owner signature", "owner-signature", "Paste the signature created by the existing owner surface");
                section.append(signature.label);
                actions.append(this.#button("Complete migration", "migration-complete", () => ({ ownerSignature: signature.input.value.trim() })));
            }
            else {
                const ownerKey = this.#input("Existing owner public key", "owner-public-key", "Public key only — never enter a seed or private key");
                section.append(ownerKey.label);
                actions.append(this.#button("Check approval", "retry"), this.#button("Use owner migration proof", "migration-begin", () => ({ ownerPublicKey: ownerKey.input.value.trim() })));
            }
        }
        if (!section.contains(heading))
            section.append(heading, copy);
        if (actions.childElementCount > 0)
            section.append(actions);
        main.append(section);
        root.append(style, main);
    }
    #button(label, action, detail = () => ({})) {
        const button = document.createElement("button");
        button.type = "button";
        button.textContent = label;
        button.dataset.action = action;
        button.addEventListener("click", () => this.dispatchEvent(new CustomEvent("pairing-action", {
            bubbles: true,
            composed: true,
            detail: { action, ...detail() },
        })));
        return button;
    }
    #input(labelText, name, placeholder) {
        const label = document.createElement("label");
        label.textContent = labelText;
        const input = document.createElement("input");
        input.name = name;
        input.placeholder = placeholder;
        input.autocomplete = "off";
        input.spellcheck = false;
        label.append(input);
        return { input, label };
    }
}
//# sourceMappingURL=pairing-view.js.map